import React from 'react';
import { useQuery } from 'react-apollo';
import { BasePage, Helmet, FourOhFourErrorPage, SurveyTheme } from '@sm/webassets';
import { t } from '@sm/intl';
import Survey from './Survey';
import { GetSurveyDetailsQuery, GetSurveyDetailsQueryVariables } from '~lib/generatedGqlTypes';

import { getSurveyIdFromCollector, getSurveyDetails } from './SurveyTaking.graphql';

import SurveyProvider from './SurveyContext';
import CurrentPageProvider from './CurrentPageContext';

const COPY = {
  META_DESCRIPTION: {
    id: 'SurveyTaking.metaDescription',
    defaultMessage:
      "Web survey powered by SurveyMonkey.com. Create your own online survey now with SurveyMonkey's expert certified FREE templates.",
  },
};

const SurveyTaking: React.FC = () => {
  //
  /**
   * TODO: get url from StaticContext, convert slug to collectorId.
   *
   * '55804393' (Tarun) for @see https://www.monkeytest3.com/rx/77JC2ZQ
   * '510967475' (Thomas) for @see https://www.monkeytest1.com/rx/P8VKGS9
   * '28753656' (Aldrin) for @see https://www.monkeytest1.com/rx/YMMCSS2
   * '510854224' (Douglas) for @see https://www.monkeytest1.com/r/K58WSQ3
   * '100056740' (Rob) for @see https://localmonkey.com/rx/D7NFHXK
   */
  const collectorId = '510967475';

  const {
    loading: loadingCollector,
    error: errorCollector,
    data: collectorData,
  } = useQuery(getSurveyIdFromCollector, {
    variables: {
      collectorId,
    },
  });

  if (errorCollector) {
    throw errorCollector;
  }

  const {
    loading: loadingSurvey,
    error: errorSurvey,
    data: surveyData,
  } = useQuery<GetSurveyDetailsQuery, GetSurveyDetailsQueryVariables>(getSurveyDetails, {
    skip: !collectorData || loadingCollector,
    variables: {
      surveyId: collectorData?.collector?.surveyId,
    },
  });

  if (errorSurvey) {
    throw errorSurvey;
  }

  if (!loadingSurvey && surveyData?.survey === null) {
    return <FourOhFourErrorPage />;
  }
  if (surveyData) {
    const surveyId = collectorData?.collector?.surveyId;
    const surveyTitle = surveyData?.survey?.title;
    const langCode = surveyData?.survey?.language?.code;
    return (
      <BasePage
        actionFlow="SurveyTaking"
        pageId="SurveyTaking"
        legacyWeb="respweb"
        // are the following from survey design response?
        includeHeader={false}
        includeFooter={false}
        includeExperiments={false}
        pageMetricsAttributes={null}
      >
        <Helmet htmlAttributes={{ lang: langCode }}>
          <title>{surveyTitle} Survey</title>
          <meta name="description" content={t(COPY.META_DESCRIPTION)} />
          <meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=yes" />
          <meta httpEquiv="content-language" content={langCode} />
        </Helmet>
        <SurveyTheme surveyId={surveyId} targetEndPoint="surveyTaking">
          <SurveyProvider survey={surveyData.survey}>
            <CurrentPageProvider>
              <Survey />
            </CurrentPageProvider>
          </SurveyProvider>
        </SurveyTheme>
      </BasePage>
    );
  }
  return null;
};

export default SurveyTaking;
